import React, { useEffect, useState } from "react";
import DeviceList from "./DeviceList";
import StructureList from "./StructureList";
import SpanList from "./SpanList";
import WeatherSensorList from "./WeatherSensorList";
import { Tab, Tabs } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useAppSelector } from "./store/hooks";
import {
    selectActiveEdgeDevices,
    selectActiveSpan,
    selectActiveStructure,
    selectActiveWeatherSensors,
} from "./store/reducers/deviceLinkingAppStoreSlice";

type ClientAssetDataAndDevicesListProps = {
    className?: string;
};

const useStyles = makeStyles((theme) => ({
    listContainer: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
    },
    // Using flex: 1 allows the lists to fill the entire height of their
    // container and still be scrollable, rather than overflowing down the page.
    listComponent: {
        flex: 1,
        overflowY: "scroll",
        minHeight: 0,
    },
}));

export default function ClientAssetDataAndDevicesList(props: ClientAssetDataAndDevicesListProps): JSX.Element {
    const classes = useStyles();
    const [activeTab, setActiveTab] = useState("structures");

    const activeSpan = useAppSelector(selectActiveSpan);
    const activeStructure = useAppSelector(selectActiveStructure);
    const activeEdgeDevices = useAppSelector(selectActiveEdgeDevices);
    const activeWeatherSensors = useAppSelector(selectActiveWeatherSensors);

    useEffect(() => {
        // Switch out of structure/spans tab if the other is selected,
        // regardless of whether there are active devices.
        if (activeTab === "structures" && !activeStructure && activeSpan) {
            setActiveTab("spans");
        } else if (activeTab === "spans" && !activeSpan && activeStructure) {
            setActiveTab("structures");
        }

        // Only switch out of devices tab if there are no active devices.
        else if (activeTab === "edge-devices" && !activeEdgeDevices.length && activeSpan) {
            setActiveTab("spans");
        } else if (activeTab === "edge-devices" && !activeEdgeDevices.length && activeStructure) {
            setActiveTab("spans");
        }

        // Only switch out of weather sensors tab if there are no active weather sensors.
        else if (activeTab === "weather-sensors" && !activeWeatherSensors.length && activeSpan) {
            setActiveTab("spans");
        } else if (activeTab === "weather-sensors" && !activeWeatherSensors.length && activeStructure) {
            setActiveTab("spans");
        }
    }, [activeSpan, activeStructure, activeEdgeDevices]);

    return (
        <div className={`${classes.listContainer} ${props.className || ""}`}>
            <Tabs
                value={activeTab}
                onChange={(_, newValue) => {
                    setActiveTab(newValue);
                }}
                variant="scrollable"
                scrollButtons="auto"
            >
                <Tab value="structures" label="Structures" />
                <Tab value="spans" label="Spans" />
                <Tab value="edge-devices" label="Edge Devices" />
                <Tab value="weather-sensors" label="Weather Sensors" />
            </Tabs>
            {activeTab === "structures" && <StructureList className={classes.listComponent} />}
            {activeTab === "spans" && <SpanList className={classes.listComponent} />}
            {activeTab === "edge-devices" && <DeviceList className={classes.listComponent} />}
            {activeTab === "weather-sensors" && <WeatherSensorList className={classes.listComponent} />}
        </div>
    );
}
